import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { withRouter, RouteComponentProps, Link } from 'react-router-dom';
import {
  getCondoDetails,
  getTrustYouReview,
  ICondoDetailsState,
  condoDetailsActions,
  unitsSearchActions,
} from 'src/store/slices/index';
import { RootState } from 'src/store';
import { Routes, CONDO_SESSION_KEY, CONDO_UNITS_SESSION_KEY_LABEL } from 'src/constants/index';
import { ISessionKey } from 'src/common-types/index';
import { Map, UrlUtils, Responsive } from 'src/utils/index';
import { TrustYouReview, MobileSlider } from 'src/components/components/index';
import { CloseBlueSvg } from 'src/assets/index';
import MapMarker from 'src/assets/images/map-bed-marker.svg';
import { CondoImagesDescription } from '../condo-images-description';
import { SkeletonComponent } from '../../../hotel-details/skeleton';
import { CondoInfo } from '../condo-info';
import { UnitsSearch } from '../condo-units';
import { CondoAmenities } from '../condo-amenities';
import { CondoActivities } from '../condo-activities';
import { CondoLocation } from '../condo-location';
import { CondoPolicies } from '../condo-policies';
import { MapWrapper } from '../../../map';
import './style.scss';

interface IMapStateToProps {
  condoDetailsStore: ICondoDetailsState;
}

interface IMapDispatchToProps {
  getCondoDetails: (
    numberId: number,
    sessionKey: ISessionKey | undefined,
    isFromAdminPage: boolean,
    isMobile?: boolean,
  ) => void;
  getTrustYouReview: (trustYouId: string) => void;
  resetCondoDetails: () => void;
  resetCondoUnits: () => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {
  matches?: boolean;
  isFromAdminPage: boolean;
}

interface IState {
  active: number;
  isMapModalOpen: boolean;
  isTrustYouLoading: boolean;
}

interface IAnchor {
  id: string;
  ref: React.RefObject<HTMLDivElement>;
}

const ZERO = 0;
const SECOND_ELEMENT = 2;
const DEFAULT_ANCHOR = 0;
const TAB_POLICIES = 'tab.policies';
const TAB_AMENITIES = 'tab.amenities.facilities';
const TAB_ACTIVITIES = 'tab.activities';
const TAB_REVIEWS = 'tab.reviews';
const BIG_MAP_ZOOM = 15;
const DEBOUNCE_TIME = 100;

class CondoDetailsWrapperComponent extends React.Component<IProps, IState> {
  state: IState = {
    active: DEFAULT_ANCHOR,
    isMapModalOpen: false,
    isTrustYouLoading: false,
  };

  overviewRef: React.RefObject<HTMLDivElement> = React.createRef();
  unitsRef: React.RefObject<HTMLDivElement> = React.createRef();
  amenitiesRef: React.RefObject<HTMLDivElement> = React.createRef();
  activitiesRef: React.RefObject<HTMLDivElement> = React.createRef();
  locationRef: React.RefObject<HTMLDivElement> = React.createRef();
  policiesRef: React.RefObject<HTMLDivElement> = React.createRef();
  reviewsRef: React.RefObject<HTMLDivElement> = React.createRef();

  ITEMS_ANCHOR: IAnchor[] = [
    { id: 'tab.overview', ref: this.overviewRef },
    { id: 'tab.units.availability', ref: this.unitsRef },
    { id: 'tab.amenities.facilities', ref: this.amenitiesRef },
    { id: 'tab.activities', ref: this.activitiesRef },
    { id: 'tab.location', ref: this.locationRef },
    { id: 'tab.policies', ref: this.policiesRef },
    { id: 'tab.reviews', ref: this.reviewsRef },
  ];

  scrollView = (e: React.MouseEvent<HTMLParagraphElement>, ref: React.RefObject<HTMLElement>) => {
    this.setState({ active: +e.currentTarget.dataset.index });
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  getAnchors = (): IAnchor[] => {
    const { condo } = this.props.condoDetailsStore;
    const allDataPolicies = [
      ...(condo.condoDetails.checkInInformation || []),
      ...(condo.condoDetails.checkOutInformation || []),
      ...(condo.condoDetails.pets || []),
    ];

    return this.ITEMS_ANCHOR.filter((item) => {
      if (
        (item.id === TAB_POLICIES && isEmpty(allDataPolicies)) ||
        (item.id === TAB_AMENITIES &&
          isEmpty(condo.condoDetails.amenities) &&
          isEmpty(condo.condoDetails.facilities)) ||
        (item.id === TAB_ACTIVITIES && isEmpty(condo.condoDetails.activities)) ||
        (item.id === TAB_REVIEWS && isEmpty(condo.condoDetails.rating.trustYouId))
      ) {
        return;
      }
      return item;
    });
  };

  filtersAnchor = () => {
    return this.getAnchors().map((itemAnchor, i) => (
      <p
        key={i}
        className={`condo-details-wrapper__tab-anchors ${i === this.state.active ? 'active' : ''}`}
        onClick={(e) => this.scrollView(e, itemAnchor.ref)}
        data-index={i}
      >
        <FormattedMessage id={itemAnchor.id} />
      </p>
    ));
  };

  onClickScrollToUnits = (e: React.MouseEvent<HTMLDivElement>) => {
    this.scrollView(e, this.unitsRef);
  };

  onClickScrollToReviews = (e: React.MouseEvent<HTMLDivElement>) => {
    this.scrollView(e, this.reviewsRef);
  };

  componentDidMount() {
    const { matches, getCondoDetails, isFromAdminPage } = this.props;
    const footer: HTMLDivElement = document.querySelector('.footer');
    const condoId = Number(this.props.history.location.pathname.split('/')[SECOND_ELEMENT]);
    const values = UrlUtils.getValues();
    const sessionKey = values[CONDO_UNITS_SESSION_KEY_LABEL]
      ? values[CONDO_UNITS_SESSION_KEY_LABEL]
      : values[CONDO_SESSION_KEY];

    getCondoDetails(condoId, sessionKey as ISessionKey, isFromAdminPage, !matches);

    document.body.style.overflow = 'auto';
    footer.style.display = 'block';

    window.addEventListener('scroll', this.onScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    this.props.resetCondoDetails();
    this.props.resetCondoUnits();
  }

  componentDidUpdate() {
    const { isTrustYouLoading } = this.state;
    const { condo, loading } = this.props.condoDetailsStore;

    if (condo && !loading && condo.condoDetails.rating.trustYouId && !isTrustYouLoading) {
      this.props.getTrustYouReview(condo?.condoDetails.rating.trustYouId);
      this.setState({ isTrustYouLoading: !isTrustYouLoading });
    }
  }

  getActiveAnchor = (): IAnchor => {
    const anchors: IAnchor[] = this.getAnchors();
    const { pageYOffset } = window;
    const additionalHeight = 300;
    let activeRef: IAnchor = anchors[ZERO];

    anchors.forEach((anchor: IAnchor) => {
      const { ref } = anchor;

      if (ref && ref.current) {
        const { current } = ref;
        const height = current.getBoundingClientRect()?.height;
        const offsetTop = current.offsetTop - additionalHeight;
        const offsetBottom = offsetTop + height;

        if (height && offsetTop && pageYOffset >= offsetTop && pageYOffset <= offsetBottom) {
          activeRef = anchor;
        }
      }
    });

    return activeRef;
  };

  onScroll = debounce(() => {
    const { condo, loading } = this.props.condoDetailsStore;

    if (condo && !loading) {
      const anchor = this.getActiveAnchor();

      if (anchor && anchor.id) {
        const index = this.getAnchors().findIndex(({ id }) => id === anchor.id);

        if (index >= ZERO && this.state.active !== index) {
          this.setState({ active: index });
        }
      }
    }
  }, DEBOUNCE_TIME);

  showMap = () => {
    this.setState({ isMapModalOpen: true });
    document.body.style.overflow = 'hidden';
  };

  closeMap = () => {
    this.setState({ isMapModalOpen: false });
    document.body.style.overflow = 'initial';
  };

  render(): React.ReactNode {
    const { isMapModalOpen } = this.state;
    const { isFromAdminPage, condoDetailsStore } = this.props;
    const { condo, loading } = condoDetailsStore;

    return (
      <div className="condo-details-wrapper">
        <div className="condo-details-wrapper__back-link desktop">
          {!isFromAdminPage && (
            <Link to={`${Routes.CondoSearch}${location.search}`}>
              <FormattedMessage id="back.to.list" />
            </Link>
          )}
        </div>
        {loading ? <SkeletonComponent /> : null}
        {condo && !loading ? (
          <div className="condo-details-wrapper__wrapper">
            <div ref={this.overviewRef} />
            <div className="condo-details-wrapper__back-link mobile">
              {condo.condoDetails.isAllInclusive ? (
                <div className="condo-details-wrapper__label-all-inclusive">
                  <p className="condo-details-wrapper__label-title">
                    <FormattedMessage id="all.inclusive" />
                  </p>
                </div>
              ) : null}
              {!isFromAdminPage && (
                <Link to={`${Routes.CondoSearch}${location.search}`}>
                  <FormattedMessage id="back.to.list" />
                </Link>
              )}
            </div>
            <div
              className={`condo-details-wrapper__desktop-hotel-info ${
                condo.condoDetails.isAllInclusive ? 'all-inclusive-wrapper' : ''
              }`}
            >
              {condo.condoDetails.isAllInclusive ? (
                <div className="condo-details-wrapper__label-all-inclusive">
                  <p className="condo-details-wrapper__label-title">
                    <FormattedMessage id="all.inclusive" />
                  </p>
                </div>
              ) : null}
              <CondoInfo condo={condo} onClickScrollToReviews={this.onClickScrollToReviews} />
            </div>
            <MobileSlider images={condo.condoDetails.media || []} />
            <div className="condo-details-wrapper__details-anchor">
              {condo && this.filtersAnchor()}
            </div>
            <div className="condo-details-wrapper__mobile-hotel-info">
              <CondoInfo condo={condo} onClickScrollToReviews={this.onClickScrollToReviews} />
            </div>
            <CondoImagesDescription
              media={condo.condoDetails.media || []}
              condoName={condo.condoDetails.title}
              condoDescription={condo.condoDetails.descriptions || []}
              onClickScrollToUnits={this.onClickScrollToUnits}
              highlights={condo.condoDetails.highlights}
              isAllInclusive={condo.condoDetails.isAllInclusive}
            />
            <Responsive>
              <UnitsSearch
                condoDetails={condo.condoDetails}
                refAnchor={this.unitsRef}
                isShortStaysSupported={
                  condo.condoDetails?.daysOfCheckIn?.pointsResortCheckInDays?.length > ZERO
                }
              />
            </Responsive>
            {(!isEmpty(condo.condoDetails.amenities) ||
              !isEmpty(condo.condoDetails.facilities)) && (
              <CondoAmenities condoDetails={condo.condoDetails} refAnchor={this.amenitiesRef} />
            )}
            {!isEmpty(condo.condoDetails.accessibilities) && (
              <div className="condo-details-wrapper__accessibilities-items">
                {condo.condoDetails.accessibilities.map((item, index) => (
                  <div className="condo-details-wrapper__accessibilities-item" key={index}>
                    <span className="condo-details-wrapper__accessibilities-text">{item.name}</span>
                    <p className="condo-details-wrapper__accessibilities-category">
                      {item.category}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {!isEmpty(condo.condoDetails.activities) && (
              <CondoActivities condoDetails={condo.condoDetails} refAnchor={this.activitiesRef} />
            )}
            <CondoLocation
              condoDetails={condo.condoDetails}
              refAnchor={this.locationRef}
              showBigMap={this.showMap}
            />
            <CondoPolicies condoDetails={condo.condoDetails} refAnchor={this.policiesRef} />
            <TrustYouReview refAnchor={this.reviewsRef} />
            <div
              className="condo-details-wrapper__big-map-wrapper"
              style={{ display: isMapModalOpen ? 'block' : 'none' }}
            >
              <div className="condo-details-wrapper__big-map-top-section">
                <span className="condo-details-wrapper__big-map-close" onClick={this.closeMap}>
                  <CloseBlueSvg />
                </span>
                {condo.condoDetails.title}
              </div>
              <MapWrapper
                locations={[{ location: condo.condoDetails.location, id: condo.condoDetails.id }]}
                center={Map.getGoogleLocation(condo.condoDetails.location)}
                icon={MapMarker}
                mapOptions={{ zoom: BIG_MAP_ZOOM, disableDefaultUI: true }}
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    condoDetailsStore: state.condoDetailsStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  getCondoDetails,
  getTrustYouReview,
  resetCondoDetails: condoDetailsActions.resetState,
  resetCondoUnits: unitsSearchActions.resetState,
};

export const CondoDetailsWrapper = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(CondoDetailsWrapperComponent));
